import React from 'react';
import { CardSurface, EmptyState, sparkAssetUrl } from '@spark';
import AddDataButton from '@/features/datasets/components/AddDataButton';
import TaskPageTabEmpty from '@/features/tasks/components/TaskPageTabEmpty';

const COPY = {
    train: {
        title: 'No Data',
        subtitle: 'Upload a dataset, and your training records will appear here.',
    },
    benchmark: {
        title: 'No Benchmark Data',
        subtitle: 'Upload a benchmark dataset, and your evaluation records will appear here.',
    },
    'archived-datasets': {
        title: 'No Archived Datasets',
        subtitle: 'Records you archive from your training set will appear here.',
    },
    'archived-benchmarks': {
        title: 'No Archived Benchmarks',
        subtitle: 'Records you archive from your benchmark will appear here.',
    },
};

const UPLOADABLE_VARIANTS = new Set(['train', 'benchmark']);

const INLINE_WRAPPER_STYLE = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '40vh',
    padding: '24px 0',
};

/**
 * Empty-state body (illustration + copy + optional Add Data CTA). Shared by
 * the page-level and in-tab layouts below.
 */
function EmptyStateBody({
    title,
    subtitle,
    showAddData,
    usecase,
    uploadTarget,
}) {
    if (showAddData) {
        return (
            <div
                className="spark-empty-state spark-empty-state-horizontal spark-empty-state-medium fs-unmask"
                style={{ padding: '40px 20px', justifyContent: 'center' }}
            >
                <div
                    className="spark-empty-state-illustration spark-empty-state-illustration-medium"
                    aria-hidden="true"
                >
                    <img
                        src={sparkAssetUrl('illustrations/illustration-box-open.svg')}
                        alt=""
                    />
                </div>
                <div className="spark-empty-state-content" style={{ gap: 12 }}>
                    <p className="spark-empty-state-title">{title}</p>
                    <p className="spark-empty-state-subtitle">{subtitle}</p>
                    <div style={{ marginTop: 4 }}>
                        <AddDataButton
                            usecase={usecase}
                            placement="bottom"
                            uploadTarget={uploadTarget}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <EmptyState
            size="medium"
            title={title}
            subtitle={subtitle}
        />
    );
}

/**
 * Empty state shown in the Data Editor when a tab (or the whole editor) has
 * no records.
 *
 * - `layout="page"` — full-width TaskPageTabEmpty chrome (no side tabs), same
 *   as Evaluations / Model Details empty states.
 * - `layout="inline"` (default) — bordered card inside the active side-tab pane.
 */
const DataTableEmptyState = ({
    variant = 'train',
    title,
    subtitle,
    usecase = null,
    uploadTarget,
    layout = 'inline',
}) => {
    const copy = COPY[variant] || COPY.train;
    const resolvedTitle = title || copy.title;
    const resolvedSubtitle = subtitle || copy.subtitle;
    const showAddData = Boolean(usecase) && UPLOADABLE_VARIANTS.has(variant);

    const body = (
        <EmptyStateBody
            title={resolvedTitle}
            subtitle={resolvedSubtitle}
            showAddData={showAddData}
            usecase={usecase}
            uploadTarget={uploadTarget}
        />
    );

    if (layout === 'page') {
        return <TaskPageTabEmpty>{body}</TaskPageTabEmpty>;
    }

    return (
        <div className="data-table-empty-state" style={INLINE_WRAPPER_STYLE}>
            <CardSurface bordered padding="large" style={{ width: '100%' }}>
                {showAddData ? (
                    body
                ) : (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            padding: '40px 24px',
                        }}
                    >
                        {body}
                    </div>
                )}
            </CardSurface>
        </div>
    );
};

export default DataTableEmptyState;
